import { Button } from '@portal_components/components/Button';
import { FormCard } from '@portal_components/components/FormCard';
import { EPageViewType, Group, Invoice } from '../../types';
import { TableItem } from '@portal_components/components/TableItem';
import React from 'react';
import { InvoiceDetails } from './InvoiceDetails';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { get, useFieldArray, useFormContext } from 'react-hook-form';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { FormDropdown } from '@portal_components/components/FormDropdown';

interface CustomerInvoiceTabProps {
  readonly: boolean;
  customerContracts?: { contractId: string; contractType: string }[];
  forContract?: boolean;
  viewType?: EPageViewType;
}

export const CustomerInvoiceTab = ({ readonly, customerContracts, forContract, viewType }: CustomerInvoiceTabProps) => {
  const { fields, append, remove } = useFieldArray<Group, 'invoices'>({ name: 'invoices' });
  const {
    formState: { errors },
    getValues,
    setValue,
    watch
  } = useFormContext<Group>();
  const [currentlyExpandedInvoice, setCurrentlyExpandedInvoice] = React.useState<number | undefined>(undefined);

  const handleExpandClicked = (index: number) => () => {
    if (currentlyExpandedInvoice === index) {
      setCurrentlyExpandedInvoice(undefined);
    } else {
      setCurrentlyExpandedInvoice(index);
    }
  };

  const handleRemove = (index: number) => () => {
    if (getValues(`invoices.${index}.invoiceGroupId`)) {
      const markedForDeletion = getValues(`invoices.${index}.markedForDeletion`);
      setValue(`invoices.${index}.markedForDeletion`, !markedForDeletion);
      return;
    }
    remove(index);
  };

  const invoicesTableItem = (_: Invoice, index: number) => {
    const markedForDeletion = watch(`invoices.${index}.markedForDeletion`);
    const errorsForRecord = get(errors, `invoices.${index}`);
    const backgroundColour = errorsForRecord ? 'bg-feedback-dark-red' : 'bg-interactive-bold-green-db-12';
    return (
      <div key={`invoice-${index}`} className="mb-3">
        <div className={`pl-2 mb-1 py-1 rounded-sm ${backgroundColour}`}>
          {markedForDeletion && <p className="text-feedback-orange text-sm">Invoice Marked for Deletion</p>}
          <TableItem key={`invoice-${index}`} remove={handleRemove(index)} readonly={readonly} onExpandClicked={handleExpandClicked(index)} expanded={currentlyExpandedInvoice === index}>
            <FormTextInput<Group> title="Customer Invoice Name" name={`invoices.${index}.customerInvoiceName`} readonly={readonly} hideErrorControl mandatory />
            <FormTextInput<Group> title="Invoice Addressee" name={`invoices.${index}.invoiceAddresse`} readonly={readonly} hideErrorControl />
            {!forContract && viewType !== EPageViewType.ADD && (
              <FormDropdown<Group>
                title="Contract ID"
                name={`invoices.${index}.contractId`}
                readonly={readonly}
                hideErrorControl
                placeholder="Select Contract ID"
                elementsForDisplay={
                  customerContracts
                    ? customerContracts.map(({ contractId, contractType }) => ({
                        title: `${contractId} (${contractType})`,
                        value: contractId
                      }))
                    : []
                }
              />
            )}
            {!forContract && (
              <FormRadioButtonGroup<Group>
                title="Default Invoice Group"
                name={`invoices.${index}.defaultInvoiceGroup`}
                buttons={[
                  { value: 'Y', title: 'Yes' },
                  { value: 'N', title: 'No' }
                ]}
                readonly={readonly}
                hideErrorControl
                mandatory
              />
            )}
          </TableItem>
        </div>
        {currentlyExpandedInvoice === index && <InvoiceDetails index={index} readonly={readonly} />}
      </div>
    );
  };

  return (
    <FormCard>
      {(!fields || fields.length === 0) && <p className="text-center">There are currently no invoices</p>}
      {fields.map(invoicesTableItem)}
      {!readonly && (
        <div className="flex flex-row pb-2 justify-end">
          <Button
            text="Add Invoice"
            primary={false}
            onClick={() => {
              let defaultInvoiceGroup: 'Y' | 'N' | undefined = undefined;
              if (!forContract && fields.length === 0) {
                defaultInvoiceGroup = 'Y';
              } else if (forContract) {
                defaultInvoiceGroup = 'N';
              }
              append({
                defaultInvoiceGroup,
                contact: { primaryContact: 'N' }
              });
            }}
          />
        </div>
      )}
    </FormCard>
  );
};
